import { glyphsByNameMap, localNamesByIdentity, observeFont, observeGlyph } from './auto-qa-identity';
import { qaCorpusIndex } from './auto-qa-corpus';
import { DEFAULT_QA_N_MIN, DEFAULT_QA_PEER_M, DEFAULT_QA_MARK_PEER_M, DEFAULT_QA_ROLE_SHARE, DEFAULT_QA_SOFT_X, DEFAULT_QA_THRESHOLD_X, DEFAULT_QA_WITHIN_FONT_RATE, isCombiningMarkCodepoint, isCombiningMarkIdentity, isMarkLikeIdentity, wilsonLowerBound } from './auto-qa-stats';
export const AUTO_QA_SOURCE_ID = 'auto-qa';
let cachedContext = null;
/**
 * Match an open font against the Auto QA corpus table and return missing
 * component / missing anchor labels after Wilson, mark-system, role, and
 * within-font gates.
 */
export function matchOpenFont(font, table = qaCorpusIndex.getTable(), options = {}) {
    const context = getMatchContext(font, table, options);
    if (!context) {
        return [];
    }
    const labels = [];
    for (const observation of context.observations) {
        labels.push(...labelsForObservation(observation, context));
    }
    return sortLabels(labels);
}
export function labelsForGlyph(font, glyphName, table = qaCorpusIndex.getTable(), options = {}) {
    const context = getMatchContext(font, table, options);
    if (!context) {
        return [];
    }
    const glyph = context.glyphsByName.get(glyphName);
    if (!glyph) {
        return [];
    }
    const observation = observeGlyph(glyph, context.glyphsByName);
    if (!observation) {
        return [];
    }
    if (!sameObservation(observation, context.observationByName.get(glyphName))) {
        cachedContext = buildContextFromObservations(font, context.table, context.options, glyphsByNameMap(font), observeFont(font));
        return sortLabels(labelsForObservation(observation, cachedContext));
    }
    return sortLabels(labelsForObservation(observation, context));
}
export function invalidateQaMatchCache() {
    cachedContext = null;
}
export function matchObservations(font, observations, table, options = {}) {
    const resolved = resolveOptions(options);
    const context = buildContextFromObservations(font, table, resolved, glyphsByNameMap(font), observations);
    const labels = [];
    for (const observation of observations) {
        labels.push(...labelsForObservation(observation, context));
    }
    return sortLabels(labels);
}
function getMatchContext(font, table, options) {
    if (!table) {
        return null;
    }
    const resolved = resolveOptions(options);
    if (cachedContext && contextMatches(cachedContext, font, table, resolved)) {
        return cachedContext;
    }
    const glyphsByName = glyphsByNameMap(font);
    const observations = observeFont(font);
    cachedContext = buildContextFromObservations(font, table, resolved, glyphsByName, observations);
    return cachedContext;
}
function contextMatches(context, font, table, options) {
    return (context.font === font &&
        context.table === table &&
        context.glyphCount === font.glyphs.length &&
        context.options.X === options.X &&
        context.options.softX === options.softX &&
        context.options.nMin === options.nMin &&
        context.options.peerM === options.peerM &&
        context.options.markPeerM === options.markPeerM &&
        context.options.roleShare === options.roleShare &&
        context.options.withinFontRate === options.withinFontRate);
}
function buildContextFromObservations(font, table, options, glyphsByName, observations) {
    const markAnchorNames = new Set(table.mark_anchor_names || []);
    const hasMarkSystem = fontHasMarkSystem(font, observations);
    const observationByName = new Map();
    for (const observation of observations) {
        observationByName.set(observation.glyphName, observation);
    }
    return {
        font,
        table,
        options,
        glyphCount: font.glyphs.length,
        glyphsByName,
        observations,
        observationByName,
        nameByIdentity: localNamesByIdentity(glyphsByName),
        hasMarkSystem,
        markAnchorNames,
        peerStats: buildPeerStats(observations, table, options, markAnchorNames, hasMarkSystem)
    };
}
function labelsForObservation(observation, context) {
    const { table, options, hasMarkSystem, markAnchorNames, peerStats } = context;
    const row = table.identities[observation.identity];
    if (!row || row.n < options.nMin) {
        return [];
    }
    const labels = [];
    const observedComponents = new Set(observation.components);
    const observedAnchors = new Set(observation.anchors);
    for (const candidate of candidateSlots(row, options, markAnchorNames, hasMarkSystem)) {
        const observed = candidate.slot === 'components'
            ? observedComponents
            : observedAnchors;
        if (observed.has(candidate.missing)) {
            continue;
        }
        if (!shouldEmitSlot(candidate.confidence, options, peerStats.get(peerKey(candidate.slot, candidate.missing)) || {
            peerCount: 0,
            rate: 0
        }, candidate.markRelated)) {
            continue;
        }
        labels.push({
            glyph_name: observation.glyphName,
            identity: observation.identity,
            kind: candidate.kind,
            missing: candidate.missing,
            displayName: displayNameForSlot(candidate, context),
            n: candidate.n,
            k: candidate.k,
            confidence: candidate.confidence
        });
    }
    const orderLabel = wrongComponentOrderLabel(observation, context);
    if (orderLabel) {
        labels.push(orderLabel);
    }
    return labels;
}
function candidateSlots(row, options, markAnchorNames, hasMarkSystem) {
    const slots = [];
    if (usuallyHas(row.k_has_any_component, row.n, options.roleShare)) {
        for (const [missing, k] of Object.entries(row.components || {})) {
            const markRelated = isCombiningMarkIdentity(missing);
            if (markRelated && !hasMarkSystem) {
                continue;
            }
            const candidate = slotIfViable(row, 'missing_component', 'components', missing, k, markRelated, options.softX);
            if (candidate) {
                slots.push(candidate);
            }
        }
    }
    if (usuallyHas(row.k_has_any_anchor, row.n, options.roleShare)) {
        for (const [missing, k] of Object.entries(row.anchors || {})) {
            const markRelated = markAnchorNames.has(missing);
            if (markRelated && !hasMarkSystem) {
                continue;
            }
            const candidate = slotIfViable(row, 'missing_anchor', 'anchors', missing, k, markRelated, options.softX);
            if (candidate) {
                slots.push(candidate);
            }
        }
    }
    return slots;
}
function slotIfViable(row, kind, slot, missing, k, markRelated, softX) {
    const n = effectiveN(row, markRelated);
    if (!couldReachSoftX(k, n, softX)) {
        return null;
    }
    const confidence = wilsonLowerBound(k, n);
    if (confidence < softX) {
        return null;
    }
    return { kind, slot, missing, k, n, confidence, markRelated };
}
function buildPeerStats(observations, table, options, markAnchorNames, hasMarkSystem) {
    const tallies = new Map();
    for (const observation of observations) {
        const row = table.identities[observation.identity];
        if (!row || row.n < options.nMin) {
            continue;
        }
        const observedComponents = new Set(observation.components);
        const observedAnchors = new Set(observation.anchors);
        for (const candidate of candidateSlots(row, options, markAnchorNames, hasMarkSystem)) {
            const key = peerKey(candidate.slot, candidate.missing);
            const tally = tallies.get(key) || { peerCount: 0, have: 0 };
            tally.peerCount += 1;
            const observed = candidate.slot === 'components'
                ? observedComponents
                : observedAnchors;
            if (observed.has(candidate.missing)) {
                tally.have += 1;
            }
            tallies.set(key, tally);
        }
    }
    const stats = new Map();
    for (const [key, tally] of tallies) {
        stats.set(key, {
            peerCount: tally.peerCount,
            rate: tally.peerCount === 0 ? 0 : tally.have / tally.peerCount
        });
    }
    return stats;
}
function peerKey(slot, missing) {
    return `${slot}:${missing}`;
}
function resolveOptions(options) {
    return {
        X: options.X ?? DEFAULT_QA_THRESHOLD_X,
        softX: options.softX ?? DEFAULT_QA_SOFT_X,
        nMin: options.nMin ?? DEFAULT_QA_N_MIN,
        peerM: options.peerM ?? DEFAULT_QA_PEER_M,
        markPeerM: options.markPeerM ?? DEFAULT_QA_MARK_PEER_M,
        roleShare: options.roleShare ?? DEFAULT_QA_ROLE_SHARE,
        withinFontRate: options.withinFontRate ?? DEFAULT_QA_WITHIN_FONT_RATE
    };
}
function sameObservation(left, right) {
    if (!right) {
        return false;
    }
    return (left.identity === right.identity &&
        left.components.join('\0') === right.components.join('\0') &&
        left.componentSequences
            .map((sequence) => sequence.join(','))
            .join('|') ===
            right.componentSequences
                .map((sequence) => sequence.join(','))
                .join('|') &&
        left.anchors.join('\0') === right.anchors.join('\0'));
}
function usuallyHas(k, n, roleShare) {
    if (n <= 0) {
        return false;
    }
    return k / n >= roleShare;
}
function couldReachSoftX(k, n, softX) {
    return n > 0 && k / n >= softX;
}
function effectiveN(row, markRelated) {
    if (markRelated && (row.n_mark_system || 0) > 0) {
        return row.n_mark_system;
    }
    return row.n;
}
function fontHasMarkSystem(font, observations) {
    for (const observation of observations) {
        if (isCombiningMarkIdentity(observation.identity)) {
            return true;
        }
        if (observation.components.some(isCombiningMarkIdentity)) {
            return true;
        }
    }
    for (const glyph of font.glyphs) {
        for (const codepoint of glyph.codepoints || []) {
            if (typeof codepoint === 'number' &&
                isCombiningMarkCodepoint(codepoint)) {
                return true;
            }
        }
    }
    return false;
}
function shouldEmitSlot(confidence, options, withinFont, markRelated) {
    if (confidence < options.softX) {
        return false;
    }
    const minPeers = markRelated ? options.markPeerM : options.peerM;
    const corroborated = withinFont.peerCount >= minPeers &&
        withinFont.rate >= options.withinFontRate;
    const contradicted = withinFont.peerCount >= minPeers &&
        withinFont.rate < options.withinFontRate;
    if (confidence >= options.X) {
        return !contradicted;
    }
    return corroborated;
}
function sortLabels(labels) {
    return labels.sort((left, right) => {
        if (right.confidence !== left.confidence) {
            return right.confidence - left.confidence;
        }
        if (left.glyph_name !== right.glyph_name) {
            return left.glyph_name.localeCompare(right.glyph_name);
        }
        if (left.kind !== right.kind) {
            return left.kind.localeCompare(right.kind);
        }
        return left.missing.localeCompare(right.missing);
    });
}
export function formatQaLabel(label) {
    return formatQaMessageText(label, true);
}
export function formatQaPlainMessage(label) {
    return formatQaMessageText(label, false);
}
export function toGlyphQaMessages(labels) {
    return labels.map((label) => ({
        sourceId: AUTO_QA_SOURCE_ID,
        severity: 'warning',
        checkId: label.kind,
        message: formatQaPlainMessage(label),
        messageId: `${label.kind}:${label.missing}`
    }));
}
function formatQaMessageText(label, markdownNames) {
    const wrap = (value) => (markdownNames ? `\`${value}\`` : value);
    const name = wrap(label.displayName || label.missing);
    if (label.kind === 'wrong_component_order') {
        const base = wrap(label.relatedDisplayName || 'the base');
        return `The mark ${name} usually comes after ${base}, not before.`;
    }
    if (label.kind === 'missing_component') {
        return `Similar fonts usually include the component ${name}. This glyph does not.`;
    }
    return `Similar fonts usually have an anchor named ${name}. This glyph does not.`;
}
function displayNameForIdentity(identity, context) {
    return context.nameByIdentity.get(identity) || identity;
}
function firstMarkBeforeBase(sequence) {
    let mark = null;
    for (const identity of sequence) {
        if (isMarkLikeIdentity(identity)) {
            if (!mark) {
                mark = identity;
            }
            continue;
        }
        if (mark) {
            return { mark, base: identity };
        }
    }
    return null;
}
function wrongComponentOrderLabel(observation, context) {
    for (const sequence of observation.componentSequences) {
        const pair = firstMarkBeforeBase(sequence);
        if (!pair) {
            continue;
        }
        return {
            glyph_name: observation.glyphName,
            identity: observation.identity,
            kind: 'wrong_component_order',
            missing: `${pair.mark}>${pair.base}`,
            displayName: displayNameForIdentity(pair.mark, context),
            relatedDisplayName: displayNameForIdentity(pair.base, context),
            n: 0,
            k: 0,
            confidence: 0.5
        };
    }
    return null;
}
function displayNameForSlot(candidate, context) {
    if (candidate.kind === 'missing_anchor') {
        return candidate.missing;
    }
    return context.nameByIdentity.get(candidate.missing) || candidate.missing;
}
